import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {SelectTemplateFromTemplateCollectionsQuery} from 'notebook/entry-templates/SelectTemplateCollectionItemTree/__generated/SelectTemplateFromTemplateCollectionsQuery';
import {
  CollectibleType,
  SELECT_TEMPLATE_FROM_TEMPLATECOLLECTIONS_QUERY,
  TemplateCollectionBrowserText,
} from 'notebook/entry-templates/SelectTemplateCollectionItemTree/constants';
import {TemplateCollectionWithChildrenQuery} from 'notebook/entry-templates/SelectTemplateCollectionItemTree/types';
import {
  collectionsToSelectTreeItems,
  FAVORITE_FOLDER_ID,
} from 'notebook/entry-templates/SelectTemplateCollectionItemTree/utils';
import notifications from 'notifications';
import EntityStore from 'stores/EntityStore';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import {GRAY_DARKER, NEAR_BLACK} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {Size} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import Link from 'ui-library/Link';
import SelectTree from 'ui-library/select-tree/SelectTree';
import {TreeItem} from 'ui-library/select-tree/SelectTreeConstants';

const EMPTY_SEARCH_QUERY = '';

const styles = StyleSheet.create({
  centerVertically: {
    display: 'flex',
    alignItems: 'center',
  },
  centerColumnHorizontally: {
    height: '20vh',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  emptyComponent: {
    paddingLeft: '20%',
    paddingRight: '20%',
  },
  headerDescription: {
    margin: '0px',
    color: NEAR_BLACK,
    ...FONT_PROXIMA_13,
  },
  darkGray: {
    color: GRAY_DARKER,
  },
  icon: {
    margin: '20px',
    width: '69px',
    height: '69px',
  },
});

const customEmptyComponent = (collectibleType: CollectibleType): JSX.Element => {
  const {name, namePlural} = TemplateCollectionBrowserText[collectibleType];

  return (
    <div className={css(styles.emptyComponent, styles.centerColumnHorizontally, styles.centerVertically)}>
      <BenchlingIcon className={css(styles.icon, styles.darkGray)} icon="benchling" />
      <div className={css(styles.headerDescription)}>
        No {namePlural} found, yet.{' '}
        <Link href={`/${EntityStore.currentUser()?.url_identifier}/template-collections`}>
          Create {name}
        </Link>{' '}
      </div>
    </div>
  );
};

export interface SelectTemplateCollectionItemTreeProps {
  collectibleType: CollectibleType;
  collectibleId: string | null;
  setCollectible: (template: {name: string; id: string} | null) => void;
  filterTemplateIds?: Array<string>;
  // Even when publishing enabled, we may want to show unpublished collectibles in the tree
  showUnpublishedCollectibles?: boolean;
  autoFocus?: boolean;
  // When true, hides templates that are associated with ProcessTaskSchemas (execution templates)
  excludeTaskSchemaExecutionTemplates?: boolean;
}

export function SelectTemplateCollectionItemTree(
  props: SelectTemplateCollectionItemTreeProps
): JSX.Element | null {
  const {
    collectibleId,
    setCollectible,
    collectibleType,
    showUnpublishedCollectibles,
    excludeTaskSchemaExecutionTemplates = !BenchlingConfig.ELN_INCLUDE_EXECUTION_TEMPLATES,
  } = props;
  const [searchQuery, setSearchQuery] = useState(EMPTY_SEARCH_QUERY);
  const [items, setItems] = useState<Array<TreeItem>>([]);
  const {apolloQuery, name, namePlural, idStartsWith, headers, columnWidths} =
    TemplateCollectionBrowserText[collectibleType];

  // Only pass excludeTaskSchemaExecutionTemplates for template queries (not subtemplate queries)
  const queryVariables = collectibleType === 'TEMPLATE' ? {excludeTaskSchemaExecutionTemplates} : undefined;

  const result = useBenchlingQuery<TemplateCollectionWithChildrenQuery>(apolloQuery, {
    fetchPolicy: 'network-only',
    variables: queryVariables,
  });
  const data = result.type === QueryResultType.ERROR ? undefined : result.data;

  // Using useEffect to have selectTree open while favorites are changed
  useEffect(() => {
    if (!data) {
      return;
    }
    const newItems = collectionsToSelectTreeItems(data, props.filterTemplateIds, showUnpublishedCollectibles);
    setItems(newItems);
  }, [data, props.filterTemplateIds, showUnpublishedCollectibles]);

  const starredCollectionIds = useMemo(
    () => new Set(data?.templateCollections.filter((c) => c.star?.isStarred).map((c) => c.id) ?? []),
    [data]
  );

  // Favorites should always be at the top of the list, followed by starred collections, then unstarred collections
  const sortRootItems = useCallback(
    (itemsToSort: Array<TreeItem>): Array<TreeItem> =>
      itemsToSort.sort((item1, item2) => {
        if (item1.id === FAVORITE_FOLDER_ID) {
          return -1;
        } else if (item2.id === FAVORITE_FOLDER_ID) {
          return 1;
        }

        const isItem1Starred = starredCollectionIds.has(item1.id);
        const isItem2Starred = starredCollectionIds.has(item2.id);
        if (isItem1Starred !== isItem2Starred) {
          return isItem1Starred ? -1 : 1;
        }
        return item1.name.localeCompare(item2.name);
      }),
    [starredCollectionIds]
  );

  const onChangeSelectedIdsHandler = useCallback(
    (selectedIds: Array<string>) => {
      const selId = selectedIds[0];
      if (selId === collectibleId) {
        // Unselect a templateId
        setCollectible(null);
      } else if (typeof selId === 'string' && selId.startsWith(idStartsWith)) {
        const item = items.find((val) => val.id === selId);
        if (item) {
          setCollectible({name: item.name, id: item.id});
        }
      }
    },
    [collectibleId, idStartsWith, items, setCollectible]
  );

  useEffect(() => {
    if (result.type === QueryResultType.ERROR) {
      setCollectible(null);
    }
  }, [result.type, setCollectible]);

  if (result.type === QueryResultType.ERROR) {
    notifications.error('Error occured while loading template collections; try reloading the page.');
    return null;
  }

  return (
    <>
      {result.type === QueryResultType.PENDING && items.length === 0 ? (
        <CenteredHorizontalThrobber />
      ) : items.length > 0 ? (
        <>
          <TextInput
            placeholder={`Search by template collection or ${namePlural}...`}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            size={Size.SMALL}
            autoFocus={props.autoFocus}
          />
          <SelectTree
            items={items}
            headers={headers}
            columnWidths={columnWidths}
            multiple={false}
            selectedIds={collectibleId ? [collectibleId] : []}
            shouldToggleExpandedOnClick={(clickedId) =>
              typeof clickedId === 'string' && clickedId.startsWith('tmplcol')
            }
            onChangeSelectedIds={onChangeSelectedIdsHandler}
            itemTypeName={`${name}`}
            shouldExpandNewItems={false}
            query={searchQuery}
            selectAllText={`All ${name}`}
            rootItemSortFn={sortRootItems}
          />
        </>
      ) : (
        <>{customEmptyComponent(collectibleType)}</>
      )}
    </>
  );
}

type Template = ExtractByTypename<
  SelectTemplateFromTemplateCollectionsQuery['templateCollections'][number],
  'TemplateCollectionNode'
>['children'][number];

/**
 * This fetches all of the templates from the template collections and returns them as a flat list.
 * In general, this should only be used in special cases, as it does not filter out unpublished templates.
 * Note: This does filter out execution templates (templates associated with ProcessTaskSchemas).
 */
export function useDangerouslySelectTemplateCollectionTemplateListUnfiltered(): Array<Template> {
  const result = useBenchlingQuery<SelectTemplateFromTemplateCollectionsQuery>(
    SELECT_TEMPLATE_FROM_TEMPLATECOLLECTIONS_QUERY,
    {
      fetchPolicy: 'network-only',
      variables: {excludeTaskSchemaExecutionTemplates: true},
    }
  );

  if (result.type !== QueryResultType.SUCCESS) {
    return [];
  }

  const templates = [];
  const templateCollections = result.data.templateCollections;
  for (const templateCollection of templateCollections) {
    for (const template of templateCollection.children) {
      templates.push(template);
    }
  }
  return templates;
}

export default SelectTemplateCollectionItemTree;
